import { useEffect, useState } from 'react';
import type { FilterState } from '../components/FilterDrawer';

const STORAGE_KEY = 'peak4.admin.filters.v1';

const DEFAULT_FILTERS: FilterState = {
  region: [],
  depot: [],
  department: [],
  jobRole: [],
  searchUser: [],
  ageRange: [],
  sex: [],
  shiftPattern: [],
  reliableDataOnly: false,
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

const loadFilters = (key: string): FilterState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FilterState> | null;
    if (!parsed || typeof parsed !== 'object') return null;

    return {
      region: toStringArray(parsed.region),
      depot: toStringArray(parsed.depot),
      department: toStringArray(parsed.department),
      jobRole: toStringArray(parsed.jobRole),
      searchUser: toStringArray(parsed.searchUser),
      ageRange: toStringArray(parsed.ageRange),
      sex: toStringArray(parsed.sex),
      shiftPattern: toStringArray(parsed.shiftPattern),
      // Absent from filters persisted before BT-101, so default to off rather
      // than silently narrowing an existing user's dashboards.
      reliableDataOnly: parsed.reliableDataOnly === true,
    };
  } catch {
    return null;
  }
};

export const usePersistedFilters = (options?: { storageKey?: string; defaultFilters?: FilterState }) => {
  const storageKey = options?.storageKey ?? STORAGE_KEY;
  const fallbackFilters = options?.defaultFilters ?? DEFAULT_FILTERS;

  const [filters, setFilters] = useState<FilterState>(() => {
    return loadFilters(storageKey) ?? fallbackFilters;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch {
      // ignore storage errors (e.g. disabled cookies/storage)
    }
  }, [filters, storageKey]);

  return { filters, setFilters } as const;
};
